/**
 * ComDesk — mail (REQ-020 §1).
 *
 * The mailboxes this person may work in, the messages in one, one message
 * opened, and the four ways of answering it: compose, reply, reply to all and
 * forward — with who owns the conversation and where it stands (§2, AC-004),
 * and this person's own queue across every mailbox they may read. SLA timers,
 * automation rules and the agent overlay are the rest of REQ-020 and are not
 * here yet.
 *
 * Nothing is cached in the application database — every list and every body is
 * read through to Stalwart by the backend — so this page is a view of the mail
 * server rather than a copy of it.
 *
 * Two things this screen deliberately does not decide. The address a reply
 * leaves under is the mailbox's, worked out on the server (AC-002), and who a
 * reply-all goes to is worked out there too, with our own addresses removed —
 * so this composer shows what will happen rather than computing a second
 * version of the rule. And a message is shown as sent only once the mail server
 * has taken it: there is no optimistic "sent" here, because a message that
 * failed and looked sent is one nobody re-sends.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CornerUpLeft,
  Flag,
  Forward,
  Inbox,
  Loader2,
  Lock,
  Mail,
  Paperclip,
  PenSquare,
  ReplyAll,
  Search,
  CalendarPlus,
  Send,
  ShieldAlert,
  Trash2,
  Undo2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useI18nStore } from "@/stores/i18nStore";
import {
  formatDateTime,
  formatTime,
  getLocaleString,
} from "@/utils/dateLocalization";
import apiClient from "@/services/api/apiClient";

interface Mailbox {
  local: string;
  label: string;
  address: string;
  firstResponseHours: number;
  restricted: boolean;
  configured: boolean;
}

interface Address {
  name?: string;
  email: string;
}

interface Message {
  id: string;
  threadId: string;
  subject: string;
  from: Address[];
  to: Address[];
  receivedAt: string;
  preview: string;
  isUnread: boolean;
  hasAttachment: boolean;
  mailbox: string;
  /** `$flagged`, which ComDesk calls to-do and every mail client already shows. */
  isTodo: boolean;
  /** `$junk`: marked spam, here or in somebody's mail client. */
  isJunk: boolean;
}

interface MessageDetail extends Message {
  cc: Address[];
  textBody: string;
  htmlBody: string | null;
  attachments: { name: string; type: string; size: number }[];
}

type SendMode = "compose" | "reply" | "replyAll" | "forward";

interface Draft {
  mode: SendMode;
  /** The message being answered or forwarded; absent when composing. */
  messageId?: string;
  /**
   * Which message that is, in words.
   *
   * Captured when the composer opens, and kept for the composer to name it by.
   * The draft is bound to `messageId` for the whole of its life — the server
   * derives the recipients, the subject and the threading headers from it — so
   * once the reading pane has moved on, "this message" no longer points at
   * anything the person can see. Then the composer has to say *which* message,
   * because a reply that goes to the sender of a message you are no longer
   * looking at is a reply to the wrong person.
   */
  answers?: { subject: string; from: string };
  to: string;
  cc: string;
  subject: string;
  body: string;
}

/**
 * The start a proposal suggests, as the form's input wants it.
 *
 * An instant carries its own zone and is put into the reader's; a wall clock
 * stays as written. Nothing proposed means an empty field, not a made-up time:
 * an empty field asks a question, a plausible wrong one gets accepted.
 */
function proposedStart(
  proposal: { start: string; instant?: string } | null,
): string {
  if (!proposal) return "";
  return proposal.instant
    ? toLocalInput(new Date(proposal.instant))
    : proposal.start.slice(0, 16);
}

/**
 * A moment as `<input type="datetime-local">` wants it, in the reader's zone.
 *
 * Used where the message named an instant (`2026-09-15T14:30:00Z`) rather than
 * a wall clock: the digits are not the local time, and offering them as one
 * booked a Berlin reader two hours early.
 */
function toLocalInput(when: Date): string {
  const two = (n: number) => String(n).padStart(2, "0");
  return (
    `${when.getFullYear()}-${two(when.getMonth() + 1)}-${two(when.getDate())}` +
    `T${two(when.getHours())}:${two(when.getMinutes())}`
  );
}

/** Recipients are typed as a comma-separated line, as in any mail client. */
const addressesIn = (line: string) =>
  line
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((email) => ({ email }));

type TriageAction =
  | "spam"
  | "notSpam"
  | "todo"
  | "notTodo"
  | "delete"
  | "restore";

/**
 * Which folder the list is showing.
 *
 * Junk and Trash are views, not a filing cabinet: they exist so that a message
 * put down by mistake can be got back. Without them the working list was the
 * only list, and "Not spam" — which renders only on a selected message — could
 * never be reached for the message it was written for. AC-011 puts hard
 * deletion behind a thirty-day grace period, and a grace period nobody can act
 * inside of is not one.
 */
type Folder = "working" | "junk" | "trash";

/**
 * The list is either a folder in one mailbox, or this person's own queue.
 *
 * Kept apart from `Folder` rather than added to it: a folder is a place inside
 * one mailbox and the queue spans every mailbox they may read, so making it a
 * fourth tab would have put a cross-mailbox list under a per-mailbox heading
 * and left "which mailbox am I in" without an answer.
 */
type View = "folder" | "queue";

/** Where a conversation stands (REQ-020 §2). Absent on the wire means `new`. */
type ThreadState =
  | "new"
  | "assigned"
  | "waiting_on_customer"
  | "waiting_on_us"
  | "resolved";

interface ThreadOwnership {
  mailbox: string;
  threadId: string;
  assignedTo?: string;
  assignedToEmail?: string;
  assignedAt?: string;
  state: ThreadState;
  /** The correspondent has written since the state was chosen. */
  wroteSince: boolean;
}

interface QueueItem {
  mailbox: string;
  threadId: string;
  state: ThreadState;
  assignedAt?: string;
  /** The earliest arrival: how long they have been waiting. */
  arrivedAt?: string;
  /** The latest arrival, which is a different fact and the fresher one. */
  lastArrivalAt?: string;
  respondedAt?: string;
  wroteSince: boolean;
  /**
   * The newest message on the thread and its subject, where the mail server
   * named it. Absent means the thread is real and its name is not known —
   * see `unnamed` — rather than that the thread is empty.
   */
  subject?: string;
  messageId?: string;
  lastAt?: string;
}

/**
 * The queue as the server sends it.
 *
 * Every list is optional here, and the page treats a missing one as unknown
 * rather than empty: three replicas are replaced one at a time, so during a
 * deploy an older one can answer without the fields this version added, and
 * reading `.length` off an absent list would blank the screen.
 */
interface Queue {
  items: QueueItem[];
  /** Mailboxes the sweep has never observed: not the same as no work. */
  unswept: string[];
  /** Mailboxes whose sweep has stopped, so what is listed may be stale. */
  stale: string[];
  /** Mailboxes holding their work that this server has no credentials for. */
  unreachable: string[];
  /** Mailboxes whose subjects could not be read this time. */
  unnamed: string[];
}

const STATE_LABELS: Record<ThreadState, string> = {
  new: "New",
  assigned: "Assigned",
  waiting_on_customer: "Waiting on customer",
  waiting_on_us: "Waiting on us",
  resolved: "Resolved",
};

/** A calendar entry being written, before anybody has confirmed it. */
interface Entry {
  /**
   * The message and mailbox this entry is about, captured when the form opens.
   *
   * The same rule as the composer's, and for the same reason: the form used to
   * read `selectedMessage` at submit time, so clicking another message in the
   * list and pressing Save filed the entry against the wrong conversation —
   * and switching *mailbox* nulled the selection, posting `messages/null` and
   * writing a real entry whose link back went nowhere, reported as success.
   */
  mailbox: string;
  messageId: string;
  /** What it answers, in words, for the warning when the pane moves on. */
  answers: string;
  title: string;
  /** `YYYY-MM-DDTHH:MM`, as `<input type="datetime-local">` gives it. */
  start: string;
  /** Minutes. `DEFAULT_MINUTES` until somebody changes it. */
  minutes: number;
  notes: string;
  /**
   * The text in the message the proposed time came from, where one did.
   *
   * Shown so the person can check the guess against what was written rather
   * than take it on trust — a parser that is right most of the time is exactly
   * the kind that gets believed when it is wrong.
   */
  found: string | null;
  /**
   * The start the server proposed, kept so a value nobody typed can be told
   * from one somebody did. Empty where nothing was proposed.
   */
  proposed: string;
  loading: boolean;
  /**
   * Whether the look for a time failed, as against found nothing.
   *
   * Two different facts, and they had one rendering: a request that never
   * completed printed "No date could be read out of this message", which is a
   * confident statement about a message nobody managed to read.
   */
  unchecked: boolean;
  /** The scheduler's own refusal. See `refusal` for why it is not shared. */
  refusal: string | null;
}

/**
 * How long a scheduled entry lasts unless somebody says otherwise. Named
 * because the close-when-untouched check has to compare against it: a literal
 * in two places is how the duration came to be left out of that check.
 */
const DEFAULT_MINUTES = 30;

const FOLDERS: { key: Folder; label: string }[] = [
  { key: "working", label: "Inbox" },
  { key: "junk", label: "Spam" },
  { key: "trash", label: "Trash" },
];

/**
 * What was done, in the past tense and in the words of what actually happened.
 *
 * "Moved to Trash" rather than "Deleted": the message is not destroyed —
 * REQ-020 AC-011 puts hard deletion behind a grace period and a legal-hold
 * check — and telling somebody their mail is gone when it is in a folder is the
 * kind of small untruth that gets relied on later.
 */
const TRIAGE_DONE: Record<TriageAction, string> = {
  spam: "Marked as spam and moved to Junk.",
  notSpam: "No longer marked as spam.",
  todo: "Flagged as to-do.",
  notTodo: "Taken off the to-do list.",
  delete: "Moved to Trash. It is not deleted from the server yet.",
  restore: "Put back in the Inbox.",
};

const TITLES: Record<SendMode, string> = {
  compose: "New message",
  reply: "Reply",
  replyAll: "Reply to all",
  forward: "Forward",
};

const nameOf = (address?: Address) =>
  address ? (address.name ?? address.email) : "(unknown sender)";

/** Today's mail by its time, older mail by its day — in the app language. */
const whenOf = (iso: string) => {
  if (!iso) return "";
  const at = new Date(iso);
  const language = useI18nStore.getState().language;
  const sameDay = new Date().toDateString() === at.toDateString();
  return sameDay
    ? formatTime(at, language)
    : at.toLocaleDateString(getLocaleString(language), {
        day: "numeric",
        month: "short",
      });
};

export default function ComDeskEmailPage() {
  const queryClient = useQueryClient();
  const [selectedMailbox, setSelectedMailbox] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [folder, setFolder] = useState<Folder>("working");
  const [view, setView] = useState<View>("folder");
  const [entry, setEntry] = useState<Entry | null>(null);
  const [params, setParams] = useSearchParams();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /**
   * A refusal that arrived after the form it belongs to had closed.
   *
   * Cancel is not disabled while a save is in flight, and an untouched
   * scheduler closes by itself when the reading pane moves on — so by the time
   * the server answers there may be no scheduler left to put the reason in. It
   * used to be dropped on the floor, and since the *success* path announces
   * itself globally, only the reassuring one of the two late outcomes was ever
   * rendered: a failed save looked exactly like a saved one.
   *
   * It cannot go in `refusal`, which the composer prints with "It was not
   * sent." appended — true of a send, a false statement about a calendar.
   */
  const [lateRefusal, setLateRefusal] = useState<{
    messageId: string;
    reason: string;
  } | null>(null);

  /** The server's own words where it gave any; a shrug is worse than a reason. */
  const said = (error: unknown, fallback: string) =>
    (error as { response?: { data?: { error?: { message?: string } } } })
      ?.response?.data?.error?.message ?? fallback;

  const mailboxes = useQuery({
    queryKey: ["comdesk", "mailboxes"],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { mailboxes: Mailbox[] };
      }>("/staff/comdesk/mailboxes");
      return response.data.data.mailboxes;
    },
  });

  /**
   * Open what a link asked for.
   *
   * Calendar entries made from a message carry a link back to the thread
   * (`calendar.ts`, `threadLink`), and REQ-020 §7 puts that reference at the
   * centre of the feature — it is the difference between an entry made here and
   * one retyped somewhere else. The page ignored the query string entirely, so
   * every one of those links opened ComDesk on the first mailbox with nothing
   * selected: a reference that pointed at the right conversation and showed the
   * wrong screen.
   *
   * The parameters are consumed once and cleared, so that reading a different
   * message afterwards does not fight a stale URL.
   */
  useEffect(() => {
    const wantMailbox = params.get("mailbox");
    const wantMessage = params.get("message");
    if (!wantMailbox && !wantMessage) return;
    if (wantMailbox) setSelectedMailbox(wantMailbox);
    if (wantMessage) setSelectedMessage(wantMessage);
    setParams({}, { replace: true });
  }, [params, setParams]);

  // Open the first usable mailbox rather than an empty frame. A mailbox with
  // no credentials on the server is listed but not opened — it would only show
  // an error that says nothing about the mail.
  useEffect(() => {
    if (selectedMailbox || !mailboxes.data?.length) return;
    const first = mailboxes.data.find((mailbox) => mailbox.configured);
    if (first) setSelectedMailbox(first.local);
  }, [mailboxes.data, selectedMailbox]);

  const current = useMemo(
    () => mailboxes.data?.find((mailbox) => mailbox.local === selectedMailbox),
    [mailboxes.data, selectedMailbox],
  );

  const messages = useQuery({
    queryKey: ["comdesk", "messages", selectedMailbox, search, folder],
    enabled: Boolean(selectedMailbox),
    // AC-001 asks for a message to appear within 30 seconds. Polling is the
    // fallback the criterion allows; JMAP push is the improvement.
    refetchInterval: 30_000,
    queryFn: async () => {
      const query =
        search.trim().length > 1
          ? `?q=${encodeURIComponent(search.trim())}`
          : "";
      const response = await apiClient.get<{ data: { messages: Message[] } }>(
        `/staff/comdesk/mailboxes/${selectedMailbox}/messages${query}${
          folder === "working" ? "" : `${query ? "&" : "?"}folder=${folder}`
        }`,
      );
      return response.data.data.messages;
    },
  });

  /**
   * This person's own queue, across every mailbox they may read.
   *
   * Not cached between openings: an assignment made by somebody else is the
   * ordinary case in a shared mailbox, and a queue that shows what was true
   * when the tab was last opened is a list of work that may already be done.
   */
  const queue = useQuery({
    queryKey: ["comdesk", "queue"],
    enabled: view === "queue",
    queryFn: async () => {
      const response = await apiClient.get<{ data: Queue }>(
        "/staff/comdesk/queue",
      );
      return response.data.data;
    },
  });

  const message = useQuery({
    queryKey: ["comdesk", "message", selectedMailbox, selectedMessage],
    enabled: Boolean(selectedMailbox && selectedMessage),
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { message: MessageDetail };
      }>(
        `/staff/comdesk/mailboxes/${selectedMailbox}/messages/${selectedMessage}`,
      );
      return response.data.data.message;
    },
  });

  /**
   * Who has the open message's thread, and where it stands.
   *
   * Keyed on the thread rather than the message: assignment is a property of
   * the conversation, and keying it on the message would have shown two
   * different owners for two messages of one thread.
   */
  const threadId = message.data?.threadId;
  const thread = useQuery({
    queryKey: ["comdesk", "thread", selectedMailbox, threadId],
    enabled: Boolean(selectedMailbox && threadId),
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { thread: ThreadOwnership };
      }>(`/staff/comdesk/mailboxes/${selectedMailbox}/threads/${threadId}`);
      return response.data.data.thread;
    },
  });

  /** Only people who can actually open this mailbox — the server decides. */
  const assignees = useQuery({
    queryKey: ["comdesk", "assignees", selectedMailbox],
    enabled: Boolean(selectedMailbox && threadId),
    queryFn: async () => {
      const response = await apiClient.get<{
        data: { assignees: { id: string; name: string; email: string }[] };
      }>(`/staff/comdesk/mailboxes/${selectedMailbox}/assignees`);
      return response.data.data.assignees;
    },
  });

  /**
   * Both writes invalidate the queue as well as the thread: giving a thread
   * away removes it from your own queue, and a queue that still lists it is
   * the screen telling you to do work that is no longer yours.
   */
  const afterThreadWrite = () => {
    void queryClient.invalidateQueries({ queryKey: ["comdesk", "thread"] });
    void queryClient.invalidateQueries({ queryKey: ["comdesk", "queue"] });
  };

  const assign = useMutation({
    mutationFn: async (assignee: string) => {
      const response = await apiClient.post<{
        data: { thread: ThreadOwnership };
      }>(
        `/staff/comdesk/mailboxes/${selectedMailbox}/threads/${threadId}/assignee`,
        { assignee },
      );
      return response.data.data.thread;
    },
    onSuccess: (owned) => {
      setRefusal(null);
      setNote(
        owned.assignedToEmail
          ? `Assigned to ${owned.assignedToEmail}.`
          : "Put back in the shared pile — nobody has it now.",
      );
      afterThreadWrite();
    },
    onError: (error) => {
      setNote(null);
      // Through the shared refusal, which renders without the composer's "It
      // was not sent" — nothing here was a send.
      setRefusal(said(error, "The assignee was not changed."));
    },
  });

  const changeState = useMutation({
    mutationFn: async (state: ThreadState) => {
      const response = await apiClient.post<{
        data: { thread: ThreadOwnership };
      }>(
        `/staff/comdesk/mailboxes/${selectedMailbox}/threads/${threadId}/state`,
        { state },
      );
      return response.data.data.thread;
    },
    onSuccess: (owned) => {
      setRefusal(null);
      setNote(`Marked ${STATE_LABELS[owned.state].toLowerCase()}.`);
      afterThreadWrite();
    },
    onError: (error) => {
      setNote(null);
      setRefusal(said(error, "The state was not changed."));
    },
  });

  /**
   * The thread has an owner the assignee list does not contain.
   *
   * `assignablesFor` offers only people who may still read the mailbox, so an
   * owner whose role was revoked — or who was dropped from a named-principal
   * mailbox, or disabled — matches no option. Only asked once both reads have
   * landed: while either is in flight "not in the list" means "the list is not
   * here yet".
   */
  const ownerIsUnknown = Boolean(
    thread.data?.assignedTo &&
      assignees.data &&
      !assignees.data.some((person) => person.id === thread.data?.assignedTo),
  );

  const send = useMutation({
    mutationFn: async (outgoing: Draft) => {
      const response = await apiClient.post<{
        data: {
          sent: {
            from: string;
            to: string[];
            subject: string;
            filed: boolean;
          };
        };
      }>(`/staff/comdesk/mailboxes/${selectedMailbox}/send`, {
        mode: outgoing.mode,
        ...(outgoing.messageId ? { messageId: outgoing.messageId } : {}),
        // A reply's recipients and subject are the server's to derive, so they
        // are sent only where this composer actually collected them.
        ...(outgoing.mode === "compose" || outgoing.mode === "forward"
          ? { to: addressesIn(outgoing.to), cc: addressesIn(outgoing.cc) }
          : {}),
        ...(outgoing.mode === "compose" ? { subject: outgoing.subject } : {}),
        body: outgoing.body,
      });
      return response.data.data.sent;
    },
    onSuccess: (sent) => {
      setRefusal(null);
      setDraft(null);
      // The address it actually left under, because that is the thing worth
      // knowing about a shared mailbox.
      //
      // `filed` says whether the server's copy reached Sent. Where it did not,
      // the message still went — the recipient has it — but nobody else in the
      // company will see it in the thread, and the first-response log will go
      // on counting the thread unanswered. Saying so is the whole point: a
      // plain "Sent." there is a reassuring rendering of a fact that is not.
      setNote(
        sent.filed
          ? `Sent from ${sent.from} to ${sent.to.join(", ")}.`
          : `Sent from ${sent.from} to ${sent.to.join(", ")}, but the mail ` +
              "server did not move the copy into Sent — the thread will still " +
              "look unanswered to colleagues.",
      );
      void queryClient.invalidateQueries({ queryKey: ["comdesk", "messages"] });
    },
    onError: (error) => {
      setNote(null);
      setRefusal(said(error, "The message was not sent."));
    },
  });

  /**
   * Spam, to-do and Trash.
   *
   * The two that move the message close the reading pane afterwards, because
   * the message is no longer in the folder this list is showing — leaving it
   * open would display mail that is somewhere else now, which is the same
   * mismatch the composer has. Flagging leaves it where it is, so the pane
   * stays and the button changes.
   *
   * Every action re-reads the list rather than editing it in place: what the
   * server did is the fact, and a list corrected locally is a list that agrees
   * with itself and not necessarily with the mail server.
   */
  const triage = useMutation({
    mutationFn: async (action: TriageAction) => {
      const response = await apiClient.post<{
        data: { triaged: { action: TriageAction; movedTo?: "junk" | "trash" } };
      }>(
        `/staff/comdesk/mailboxes/${selectedMailbox}/messages/${selectedMessage}/triage`,
        { action },
      );
      return response.data.data.triaged;
    },
    onSuccess: (triaged) => {
      setRefusal(null);
      setNote(TRIAGE_DONE[triaged.action]);
      // The message has left the folder on screen, whichever direction it went,
      // so the pane stops showing it.
      if (triaged.movedTo) setSelectedMessage(null);
      // The draft is deliberately left alone. It is bound to its own message
      // and holds somebody's words, and triaging message B is no reason to
      // discard a half-written reply to A — which is exactly what clearing it
      // here did, silently, while the banner reported only "Moved to Trash".
      // Where the draft answers the message just moved, the warning above it
      // already names which message that is; the words stay the person's.
      void queryClient.invalidateQueries({ queryKey: ["comdesk", "messages"] });
      void queryClient.invalidateQueries({ queryKey: ["comdesk", "message"] });
    },
    onError: (error) => {
      setNote(null);
      // The server's own words, so "this mailbox has no Trash folder" does not
      // reach the person as "something went wrong".
      setRefusal(said(error, "The message was not changed."));
    },
  });

  /**
   * The scheduler: what the composer is to a reply, this is to a calendar entry.
   *
   * Opened with a proposal fetched from the server, never with an entry made.
   * AC-013 is explicit that a time read out of a message is a suggestion for a
   * person to edit and not a booking on their behalf, so nothing here reaches
   * the calendar until Save.
   */
  const openScheduler = async () => {
    setNote(null);
    setLateRefusal(null);
    const mailbox = selectedMailbox;
    const messageId = selectedMessage;
    if (!mailbox || !messageId) return;

    setEntry({
      mailbox,
      messageId,
      answers: message.data?.subject ?? "",
      title: message.data?.subject ?? "",
      start: "",
      proposed: "",
      minutes: DEFAULT_MINUTES,
      notes: "",
      found: null,
      loading: true,
      unchecked: false,
      refusal: null,
    });
    try {
      const response = await apiClient.get<{
        data: {
          title: string;
          proposal: {
            start: string;
            found: string;
            instant?: string;
          } | null;
        };
      }>(
        `/staff/comdesk/mailboxes/${mailbox}/messages/${messageId}/calendar/proposal`,
      );
      const { title, proposal } = response.data.data;
      setEntry((open) =>
        // Only the fields nobody has touched, and only if this is still the
        // same message. The response used to replace the whole object, so
        // anything typed while the request was in flight — the form is editable
        // straight away — was thrown away without a word.
        !open || open.messageId !== messageId
          ? open
          : {
              ...open,
              title: open.title.trim() ? open.title : title,
              // An instant carries its own zone; put it in the reader's. A wall
              // clock stays as written. No proposal means an empty field, not a
              // made-up time: an empty field asks a question, a plausible wrong
              // one gets accepted.
              start: open.start ? open.start : proposedStart(proposal),
              proposed: open.start ? open.proposed : proposedStart(proposal),
              // `found` captions the field, so it belongs to whatever is *in*
              // the field. Setting it unconditionally put "proposed from … in
              // the message" above a time the person had typed themselves
              // while the request was still in flight.
              found: open.start ? open.found : (proposal?.found ?? null),
              loading: false,
              unchecked: false,
            },
      );
    } catch {
      // A proposal that cannot be fetched is not a reason to refuse the entry —
      // the person can type the time themselves. What it is not is proof the
      // message had no date in it, and saying so would be a confident statement
      // about a message nobody managed to read.
      setEntry((open) =>
        !open || open.messageId !== messageId
          ? open
          : { ...open, loading: false, unchecked: true },
      );
    }
  };

  const schedule = useMutation({
    mutationFn: async (draft: Entry) => {
      const response = await apiClient.post<{
        data: {
          entry: { id: string; calendar: string; isOwnCalendar: boolean };
        };
      }>(
        // The mailbox and message the form was *opened* on, never whatever is
        // selected now.
        `/staff/comdesk/mailboxes/${draft.mailbox}/messages/${draft.messageId}/calendar`,
        {
          title: draft.title,
          start: `${draft.start}:00`,
          duration: `PT${draft.minutes}M`,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
          ...(draft.notes.trim() ? { notes: draft.notes } : {}),
        },
      );
      return response.data.data.entry;
    },
    onSuccess: (created, draft) => {
      setLateRefusal(null);
      // Bound to the entry it was fired for, like the proposal above. Cancel is
      // not disabled while a save is in flight, so a request from a form the
      // person has already closed and reopened elsewhere would otherwise wipe
      // the new one — including anything typed into it.
      setEntry((open) =>
        open && open.messageId !== draft.messageId ? open : null,
      );
      // Which calendar it landed in, because the person's own and the shared
      // mailbox's are different things and only one of them is private. Saying
      // "Added to your calendar" for the shared one would be the reassuring
      // half of a fact that has an unreassuring half.
      setNote(
        created.isOwnCalendar
          ? `Added to your calendar (${created.calendar}).`
          : `Added to the shared ${created.calendar} calendar — you have no ` +
              "mail account of your own on this server, so everyone who can " +
              "read this mailbox can see it.",
      );
    },
    onError: (error, draft) => {
      setNote(null);
      const reason = said(error, "Nothing was scheduled.");
      // The scheduler's own refusal, not the shared one. `refusal` is rendered
      // inside the composer with the words "It was not sent." appended, so a
      // calendar failure shown there was a false statement about a send that
      // never happened — and both forms can be open at once.
      setEntry((open) =>
        open && open.messageId === draft.messageId
          ? { ...open, refusal: reason }
          : open,
      );
      // And record it against its message either way. Which of the two banners
      // shows is decided when rendering, by whether that scheduler is still
      // open — not here: a state updater does not run synchronously, so asking
      // it what happened would have answered before it had.
      setLateRefusal({ messageId: draft.messageId, reason });
    },
  });

  /**
   * A scheduler nobody has typed in closes when the reading pane moves on; one
   * holding work stays and says which message it is about. The same rule as the
   * composer's, which was written for this exact defect and then not applied to
   * the second form.
   */
  useEffect(() => {
    if (!entry || entry.messageId === selectedMessage) return;
    // "Untouched" has to mean *nobody typed*, not "no value present": a found
    // proposal fills `start`, so requiring it to be empty meant the guard never
    // fired in the ordinary case and the form stayed open beside the wrong
    // message. What was proposed is remembered so it can be told from what was
    // typed over it.
    // Every field, including the one that has no proposal behind it: the
    // duration was left out, so changing 30 minutes to 90 and then clicking
    // another message threw the edit away with the form and said nothing.
    const untouched =
      !entry.notes.trim() &&
      entry.title === entry.answers &&
      entry.start === entry.proposed &&
      entry.minutes === DEFAULT_MINUTES;
    if (untouched) setEntry(null);
  }, [selectedMessage, entry]);

  /** Open the composer. The draft it starts from is the mode and nothing else. */
  const startDraft = (mode: SendMode) => {
    setRefusal(null);
    setNote(null);
    const answered = message.data;
    setDraft({
      mode,
      ...(mode === "compose"
        ? {}
        : {
            messageId: selectedMessage ?? "",
            // Named here rather than read from `message.data` at render time,
            // because by then it may be a different message entirely — which
            // is the whole of the bug this guards.
            ...(answered
              ? {
                  answers: {
                    subject: answered.subject,
                    from: answered.from[0]?.email ?? "an unknown sender",
                  },
                }
              : {}),
          }),
      to: "",
      cc: "",
      subject: "",
      body: "",
    });
  };

  /**
   * An open composer answers the message it was opened on, and only that one.
   *
   * `messageId` is captured when the composer opens and never re-aimed: the
   * server derives the recipients, the subject and the threading headers from
   * it. But the reading pane moves when somebody clicks another message in the
   * list — a normal "let me just check something" — and the composer used to
   * stay put beside it, saying "goes to the sender of this message" while still
   * carrying the first message's id. Typing and sending there answered the
   * message that had scrolled away, in the words meant for the new one: the
   * first correspondent got a reply to somebody else's question, and the second
   * got nothing.
   *
   * Nothing is silently re-aimed, because re-aiming would send text written
   * about one thread to another. Instead: a composer nobody has typed in yet is
   * simply closed, and one holding work is kept and made to say which message
   * it answers (see `answers`). The choice of what to do with the words stays
   * with the person who wrote them.
   */
  useEffect(() => {
    if (!draft?.messageId || draft.messageId === selectedMessage) return;
    const untouched =
      !draft.body.trim() &&
      !draft.to.trim() &&
      !draft.cc.trim() &&
      !draft.subject.trim();
    if (untouched) setDraft(null);
  }, [selectedMessage, draft]);

  if (mailboxes.isLoading) {
    return (
      <div className="flex items-center gap-2 p-8 text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading mailboxes…
      </div>
    );
  }

  if (mailboxes.isError) {
    return (
      <div className="m-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        ComDesk could not read the mailbox list. The mail server may be
        unreachable from the API.
      </div>
    );
  }

  if (!mailboxes.data?.length) {
    return (
      <div className="m-6 rounded-lg border border-gray-200 bg-white p-6">
        <h1 className="text-lg font-semibold text-gray-900">ComDesk</h1>
        <p className="mt-2 text-sm text-gray-600">
          Your roles give you no mailbox to work in. Mailbox access follows
          staff roles, and <code>dpo@</code> and <code>privacy@</code> are
          restricted to named individuals rather than to a role.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col">
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Mail className="h-5 w-5 text-sky-600" /> ComDesk
        </h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => startDraft("compose")}
            disabled={!current?.configured}
            className="flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:bg-gray-300"
          >
            <PenSquare className="h-4 w-4" /> Compose
          </button>
          <label className="relative w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setSelectedMessage(null);
              }}
              placeholder="Search this mailbox"
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-sky-500 focus:outline-none"
            />
          </label>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[14rem_22rem_1fr]">
        {/* Mailboxes */}
        <aside className="overflow-y-auto border-r border-gray-200 bg-gray-50 p-3">
          {mailboxes.data.map((mailbox) => (
            <button
              key={mailbox.local}
              disabled={!mailbox.configured}
              onClick={() => {
                setSelectedMailbox(mailbox.local);
                setSelectedMessage(null);
                setFolder("working");
              }}
              title={
                mailbox.configured
                  ? mailbox.address
                  : `${mailbox.address} — no credentials on this server`
              }
              className={`mb-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                mailbox.local === selectedMailbox
                  ? "bg-sky-100 font-medium text-sky-700"
                  : mailbox.configured
                    ? "text-gray-700 hover:bg-gray-100"
                    : "cursor-not-allowed text-gray-400"
              }`}
            >
              <Inbox className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 truncate">{mailbox.label}</span>
              {mailbox.restricted && (
                <Lock
                  className="h-3 w-3 text-gray-400"
                  aria-label="Named individuals only"
                />
              )}
            </button>
          ))}
        </aside>

        {/* Messages */}
        <section className="overflow-y-auto border-r border-gray-200">
          {/*
            Spam and Trash are reachable, not merely written to. A message put
            down by mistake has to be findable again, or "moves to Trash, never
            destroys" is true of the mail server and false of anything a person
            can do about it.
          */}
          <div
            role="tablist"
            aria-label="Folder"
            className="flex gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1.5"
          >
            {FOLDERS.map((one) => (
              <button
                key={one.key}
                role="tab"
                aria-selected={view === "folder" && folder === one.key}
                onClick={() => {
                  setView("folder");
                  setFolder(one.key);
                  setSelectedMessage(null);
                }}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  view === "folder" && folder === one.key
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {one.label}
              </button>
            ))}
            {/*
              Set apart from the folders by a rule, because it is a different
              kind of list: every mailbox this person may read rather than one
              place inside the mailbox they are looking at.
            */}
            <button
              role="tab"
              aria-selected={view === "queue"}
              onClick={() => {
                setView("queue");
                setSelectedMessage(null);
              }}
              className={`ml-auto rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                view === "queue"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              My queue
            </button>
          </div>

          {view === "queue" && (
            <div>
              {queue.isLoading && (
                <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Reading your queue…
                </p>
              )}

              {/*
                A queue that could not be read is not an empty queue, and the
                two must not share a rendering — the empty one says there is
                nothing to do.
              */}
              {queue.isError && (
                <p
                  role="alert"
                  className="m-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                >
                  Your queue could not be read, so this is not a list of what
                  you have. Try again.
                </p>
              )}

              {queue.data && queue.data.items?.length === 0 && (
                <p className="p-4 text-sm text-gray-500">
                  Nothing is assigned to you.
                </p>
              )}

              {queue.data?.items?.map((item) => (
                <button
                  key={`${item.mailbox}/${item.threadId}`}
                  disabled={!item.messageId}
                  onClick={() => {
                    if (!item.messageId) return;
                    setSelectedMailbox(item.mailbox);
                    setSelectedMessage(item.messageId);
                    setView("folder");
                    setFolder("working");
                  }}
                  className="flex w-full flex-col items-start gap-1 border-b border-gray-100 px-4 py-3 text-left hover:bg-gray-50 disabled:cursor-default disabled:hover:bg-transparent"
                >
                  <span className="flex w-full items-center gap-2">
                    <span className="truncate text-sm font-medium text-gray-900">
                      {item.subject ?? "A thread the mail server did not name"}
                    </span>
                    <span className="ml-auto shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
                      {STATE_LABELS[item.state]}
                      {/*
                        The state as somebody set it, plus the fact that ends
                        it. A customer writing back is what "waiting on the
                        customer" was waiting for, and a resolved thread they
                        replied to is open again — the badge alone told people
                        not to act.
                      */}
                      {item.wroteSince ? " · they replied" : ""}
                    </span>
                  </span>
                  <span className="text-xs text-gray-500">
                    {item.mailbox}@
                    {/*
                      Two different facts, and the first version carried only
                      the older one: `arrivedAt` is how long they have been
                      waiting, `lastArrivalAt` is when they last wrote. A
                      three-week-old thread whose customer wrote this morning
                      read "arrived 3 weeks ago" beside a message nobody had
                      seen.

                      And an absent `arrivedAt` is the log not having observed
                      this thread — which is exactly the row assignment creates
                      when it assigns a thread the sweep has not reached. It is
                      not the positive claim that nobody has written.
                    */}
                    {item.arrivedAt
                      ? ` · waiting since ${whenOf(item.arrivedAt)}`
                      : " · no arrival recorded for this thread yet"}
                    {item.lastArrivalAt && item.lastArrivalAt !== item.arrivedAt
                      ? ` · last wrote ${whenOf(item.lastArrivalAt)}`
                      : ""}
                    {!item.respondedAt && item.arrivedAt ? " · unanswered" : ""}
                  </span>
                </button>
              ))}

              {/*
                Both of these are the log admitting what it does not know. An
                unswept mailbox has never been looked at, and a thread the mail
                server would not name is still a thread — saying neither would
                make an incomplete queue read as a complete one.
              */}
              {(queue.data?.unswept?.length ?? 0) > 0 && (
                <p className="m-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  {`${queue.data?.unswept?.join("@, ")}@ has not been swept yet. Work there cannot appear here until it has been.`}
                </p>
              )}
              {/*
                The flattering gap the first version left open: a mailbox whose
                sweep stopped days ago still holds old rows, so the queue kept
                listing them and said nothing about the observation behind them
                having ended.
              */}
              {(queue.data?.unreachable?.length ?? 0) > 0 && (
                <p
                  role="alert"
                  className="m-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                >
                  {`This server has no credentials for ${queue.data?.unreachable?.join("@, ")}@, so the threads listed there cannot be opened or answered from here.`}
                </p>
              )}
              {(queue.data?.stale?.length ?? 0) > 0 && (
                <p
                  role="alert"
                  className="m-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                >
                  {`The sweep over ${queue.data?.stale?.join("@, ")}@ has stopped, so anything listed from there may be out of date and newer work is missing.`}
                </p>
              )}
              {(queue.data?.unnamed?.length ?? 0) > 0 && (
                <p className="m-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  {`${queue.data?.unnamed?.join("@, ")}@ did not answer, so threads there are listed without their subjects.`}
                </p>
              )}
            </div>
          )}

          {view === "folder" && messages.isLoading && (
            <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Reading{" "}
              {current?.address}…
            </div>
          )}

          {view === "folder" && messages.isError && (
            <div className="m-3 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>
                {current?.label} did not answer. The mail server may be
                restarting; this list retries on its own.
              </span>
            </div>
          )}

          {view === "folder" && messages.data?.length === 0 && (
            <p className="p-4 text-sm text-gray-500">
              {search.trim().length > 1
                ? "Nothing matches that search."
                : "This mailbox is empty."}
            </p>
          )}

          {view === "folder" &&
            messages.data?.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedMessage(item.id)}
                className={`flex w-full flex-col gap-1 border-b border-gray-100 px-4 py-3 text-left transition-colors ${
                  item.id === selectedMessage ? "bg-sky-50" : "hover:bg-gray-50"
                }`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span
                    className={`truncate text-sm ${item.isUnread ? "font-semibold text-gray-900" : "text-gray-700"}`}
                  >
                    {nameOf(item.from[0])}
                  </span>
                  <span className="flex-shrink-0 text-xs text-gray-400">
                    {whenOf(item.receivedAt)}
                  </span>
                </div>
                <span
                  className={`truncate text-sm ${item.isUnread ? "font-medium text-gray-900" : "text-gray-600"}`}
                >
                  {item.subject}
                </span>
                <span className="flex items-center gap-1 truncate text-xs text-gray-500">
                  {item.hasAttachment && (
                    <Paperclip className="h-3 w-3 flex-shrink-0" />
                  )}
                  {item.preview}
                </span>
              </button>
            ))}
        </section>

        {/* One message */}
        <article className="overflow-y-auto p-6">
          {!selectedMessage && !draft && (
            <p className="text-sm text-gray-500">
              Select a message to read it.
            </p>
          )}

          {note && (
            <p
              role="status"
              className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800"
            >
              {note}
            </p>
          )}

          {/*
            A save that failed after its form had gone. Shown only when that
            scheduler is no longer open on that message — when it is, the form's
            own banner carries the reason and this would be a second copy.
          */}
          {lateRefusal && entry?.messageId !== lateRefusal.messageId && (
            <p
              role="alert"
              className="mb-4 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              <span>{lateRefusal.reason}</span>
            </p>
          )}

          {/*
            A refusal with no composer open — a triage that the mail server
            would not do. The composer has its own banner further down, and it
            says "It was not sent", which is only true of a send; this one
            carries the server's words and nothing else. Without it the reason
            was set and never rendered, which is a refusal the person never
            sees.
          */}
          {/* ...and not while the scheduler is open either: that form carries
              its own banner, and two copies of one refusal reads as two
              problems. */}
          {refusal && !draft && !entry && (
            <p
              role="alert"
              className="mb-4 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{refusal}</span>
            </p>
          )}

          {message.isLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Opening…
            </div>
          )}

          {message.data && (
            <>
              <h2 className="text-xl font-semibold text-gray-900">
                {message.data.subject}
              </h2>

              {/*
                Who has this conversation, and where it stands (§2, AC-004).
                On the thread, not the message: a shared mailbox's answer to
                "whose is this" has to be the same whichever message of the
                thread you happen to have open.
              */}
              <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  Assigned to
                  <select
                    aria-label="Assigned to"
                    value={thread.data?.assignedTo ?? ""}
                    disabled={
                      thread.isLoading ||
                      thread.isError ||
                      assignees.isLoading ||
                      assignees.isError ||
                      assign.isPending
                    }
                    onChange={(event) => assign.mutate(event.target.value)}
                    className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 disabled:bg-gray-100"
                  >
                    <option value="">Nobody</option>
                    {assignees.data?.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.name}
                      </option>
                    ))}
                    {/*
                      The owner, when they are no longer someone this mailbox
                      can be assigned to — disabled, role revoked, dropped from
                      the named principals. Without an option of their own the
                      browser falls back to the empty one and the control reads
                      "Nobody", which is a wrong answer with a confident face:
                      both reads succeeded, so no error banner fires either.
                    */}
                    {ownerIsUnknown && (
                      <option value={thread.data?.assignedTo}>
                        {thread.data?.assignedToEmail ?? "Somebody"} — no longer
                        able to read this mailbox
                      </option>
                    )}
                  </select>
                </label>

                <label className="flex items-center gap-2 text-xs text-gray-600">
                  State
                  <select
                    aria-label="State"
                    value={thread.data?.state ?? "new"}
                    disabled={
                      thread.isLoading ||
                      thread.isError ||
                      changeState.isPending
                    }
                    onChange={(event) =>
                      changeState.mutate(event.target.value as ThreadState)
                    }
                    className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 disabled:bg-gray-100"
                  >
                    {(Object.keys(STATE_LABELS) as ThreadState[]).map(
                      (state) => (
                        <option
                          key={state}
                          value={state}
                          // `assigned` describes an owner, so it cannot be
                          // chosen without one — the server refuses it, and an
                          // option that always fails teaches people to distrust
                          // the screen.
                          disabled={
                            state === "assigned" && !thread.data?.assignedTo
                          }
                        >
                          {STATE_LABELS[state]}
                        </option>
                      ),
                    )}
                  </select>
                </label>

                {/*
                  A control whose value could not be read must not sit there
                  looking like the current state: an unreadable assignment
                  rendering as "Nobody" is a wrong answer with a confident face.
                */}
                {thread.isError && (
                  <span
                    role="alert"
                    className="flex items-center gap-1.5 text-xs text-red-700"
                  >
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Who has this thread could not be read.
                  </span>
                )}
                {ownerIsUnknown && (
                  <span
                    role="alert"
                    className="flex items-center gap-1.5 text-xs text-amber-800"
                  >
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {thread.data?.assignedToEmail ?? "Whoever holds this"} can
                    no longer read this mailbox, so it is in nobody's queue.
                  </span>
                )}
                {thread.data?.wroteSince && (
                  <span className="flex items-center gap-1.5 text-xs text-amber-800">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    They have written since this state was set.
                  </span>
                )}
                {!thread.isError && assignees.isError && (
                  <span
                    role="alert"
                    className="flex items-center gap-1.5 text-xs text-red-700"
                  >
                    <AlertTriangle className="h-3.5 w-3.5" />
                    The list of who could take this could not be read.
                  </span>
                )}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={() => startDraft("reply")}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <CornerUpLeft className="h-4 w-4" /> Reply
                </button>
                <button
                  onClick={() => startDraft("replyAll")}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <ReplyAll className="h-4 w-4" /> Reply to all
                </button>
                <button
                  onClick={() => startDraft("forward")}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Forward className="h-4 w-4" /> Forward
                </button>

                {/*
                  Triage, set apart from the three ways of answering because
                  two of these take the message out of the list everybody else
                  works from. The labels say what will happen — "Move to Trash",
                  not "Delete" — since the message is not destroyed and telling
                  somebody it was would be the wrong half of AC-011 to promise.
                */}
                <span className="mx-1 w-px self-stretch bg-gray-200" />
                <button
                  disabled={triage.isPending}
                  onClick={() =>
                    triage.mutate(message.data!.isTodo ? "notTodo" : "todo")
                  }
                  className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50 ${
                    message.data.isTodo
                      ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Flag className="h-4 w-4" />
                  {message.data.isTodo ? "On the to-do list" : "Mark as to-do"}
                </button>
                <button
                  disabled={triage.isPending}
                  onClick={() =>
                    triage.mutate(message.data!.isJunk ? "notSpam" : "spam")
                  }
                  className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ShieldAlert className="h-4 w-4" />
                  {message.data.isJunk ? "Not spam" : "Mark as spam"}
                </button>
                {folder !== "working" && (
                  <button
                    disabled={triage.isPending}
                    onClick={() => triage.mutate("restore")}
                    className="flex items-center gap-1.5 rounded-lg border border-emerald-300 px-3 py-1.5 text-sm text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
                  >
                    <Undo2 className="h-4 w-4" /> Put back in the Inbox
                  </button>
                )}
                <button
                  disabled={triage.isPending}
                  onClick={() => triage.mutate("delete")}
                  className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" /> Move to Trash
                </button>
                <button
                  onClick={openScheduler}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <CalendarPlus className="h-4 w-4" /> Add to calendar
                </button>
              </div>

              <dl className="mt-3 space-y-1 text-sm text-gray-600">
                <div className="flex gap-2">
                  <dt className="w-12 text-gray-400">From</dt>
                  <dd>
                    {message.data.from
                      .map((address) => `${nameOf(address)} <${address.email}>`)
                      .join(", ")}
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-12 text-gray-400">To</dt>
                  <dd>
                    {message.data.to
                      .map((address) => address.email)
                      .join(", ") || "—"}
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-12 text-gray-400">At</dt>
                  <dd>
                    {formatDateTime(
                      message.data.receivedAt,
                      useI18nStore.getState().language,
                    )}
                  </dd>
                </div>
              </dl>

              {message.data.attachments.length > 0 && (
                <ul className="mt-4 flex flex-wrap gap-2">
                  {message.data.attachments.map((attachment) => (
                    <li
                      key={attachment.name}
                      className="flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-600"
                    >
                      <Paperclip className="h-3 w-3" />
                      {attachment.name}
                      <span className="text-gray-400">
                        {Math.round(attachment.size / 1024)} kB
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {/* The plain-text part is rendered, not the HTML one. Remote
                  HTML in a staff tool is a tracking pixel and a script surface
                  at once; showing it safely needs sanitising and proxying that
                  REQ-020 has not specified yet. */}
              <pre className="mt-6 whitespace-pre-wrap border-t border-gray-200 pt-6 font-sans text-sm leading-relaxed text-gray-800">
                {message.data.textBody ||
                  (message.data.htmlBody
                    ? "This message has only an HTML body. Rendering it safely is not built yet."
                    : "This message has no body.")}
              </pre>
            </>
          )}

          {entry && (
            <form
              aria-label="Add to calendar"
              onSubmit={(event) => {
                event.preventDefault();
                schedule.mutate(entry);
              }}
              className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4"
            >
              <h3 className="text-sm font-semibold text-gray-900">
                Add to calendar
              </h3>

              {/*
                The message this entry is about, when the reading pane has
                moved on to another. "This message" is only true while the two
                agree, and the entry is filed against the one it was opened on.
              */}
              {entry.messageId !== selectedMessage && (
                <p
                  role="alert"
                  className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                >
                  This is about <strong>{entry.answers}</strong>, not the
                  message shown above — that is the one it was started on.
                </p>
              )}

              {entry.loading ? (
                <p className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Looking for a time in this message…
                </p>
              ) : entry.unchecked ? (
                // Not "no date": the message could not be read at all, and
                // saying otherwise is a confident statement about something
                // nobody managed to check.
                <p className="mt-2 text-xs text-amber-800">
                  This message could not be checked for a time, so nothing is
                  proposed. Set the time yourself.
                </p>
              ) : entry.found ? (
                // The words the guess came from, so it can be checked rather
                // than believed. A parser right most of the time is the kind
                // that gets trusted on the occasion it is wrong.
                <p className="mt-2 text-xs text-gray-500">
                  Proposed from “{entry.found}” in the message. Change it if
                  that is not what was meant.
                </p>
              ) : (
                <p className="mt-2 text-xs text-gray-500">
                  No date could be read out of this message, so the time is
                  yours to set.
                </p>
              )}

              <label className="mt-3 block text-xs font-medium text-gray-600">
                Title
                <input
                  value={entry.title}
                  onChange={(event) =>
                    setEntry({ ...entry, title: event.target.value })
                  }
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-sky-500 focus:outline-none"
                />
              </label>

              <div className="mt-3 flex flex-wrap gap-3">
                <label className="block text-xs font-medium text-gray-600">
                  Starts
                  <input
                    type="datetime-local"
                    value={entry.start}
                    onChange={(event) =>
                      setEntry({ ...entry, start: event.target.value })
                    }
                    className="mt-1 block rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-sky-500 focus:outline-none"
                  />
                </label>
                <label className="block text-xs font-medium text-gray-600">
                  Minutes
                  <input
                    type="number"
                    min={5}
                    step={5}
                    value={entry.minutes}
                    onChange={(event) =>
                      setEntry({
                        ...entry,
                        minutes: Number(event.target.value),
                      })
                    }
                    className="mt-1 block w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-sky-500 focus:outline-none"
                  />
                </label>
              </div>

              <label className="mt-3 block text-xs font-medium text-gray-600">
                Notes
                <textarea
                  value={entry.notes}
                  onChange={(event) =>
                    setEntry({ ...entry, notes: event.target.value })
                  }
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-sky-500 focus:outline-none"
                />
              </label>

              {entry.refusal && (
                <p
                  role="alert"
                  className="mt-3 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                >
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>{entry.refusal}</span>
                </p>
              )}

              <div className="mt-3 flex items-center gap-2">
                <button
                  type="submit"
                  disabled={
                    schedule.isPending ||
                    entry.loading ||
                    !entry.start ||
                    !entry.title.trim()
                  }
                  className="flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:bg-gray-300"
                >
                  <CalendarPlus className="h-4 w-4" />
                  {schedule.isPending ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => setEntry(null)}
                  className="rounded-lg px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {draft && (
            <form
              aria-label={TITLES[draft.mode]}
              onSubmit={(event) => {
                event.preventDefault();
                send.mutate(draft);
              }}
              className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4"
            >
              <h3 className="text-sm font-semibold text-gray-900">
                {TITLES[draft.mode]} from {current?.address}
              </h3>

              {/*
                The message this draft answers, when the reading pane has moved
                on to another one. "This message" is only true while the two
                agree; once they do not, the words have to name the message the
                draft is actually bound to, because that is where it will go.
              */}
              {draft.messageId && draft.messageId !== selectedMessage && (
                <p
                  role="alert"
                  className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                >
                  This answers{" "}
                  <strong>
                    {draft.answers?.subject ?? "an earlier message"}
                  </strong>
                  {draft.answers ? ` from ${draft.answers.from}` : ""}, not the
                  message shown above — that is the one it was started on.
                  Discard it if you meant to answer this one.
                </p>
              )}

              {(draft.mode === "reply" || draft.mode === "replyAll") && (
                // Said rather than shown as an editable list, because the list
                // is the server's: it is derived from the message and has our
                // own addresses removed there.
                <p className="mt-1 text-xs text-gray-500">
                  {/* "this" only while the pane still shows it; otherwise the
                      sentence above has already named which message. */}
                  {draft.messageId === selectedMessage
                    ? draft.mode === "reply"
                      ? "Goes to the sender of this message."
                      : "Goes to everyone on this message, except our own addresses."
                    : draft.mode === "reply"
                      ? "Goes to the sender of that message."
                      : "Goes to everyone on that message, except our own addresses."}{" "}
                  The subject and the threading headers are taken from it.
                </p>
              )}

              {(draft.mode === "compose" || draft.mode === "forward") && (
                <>
                  <label className="mt-3 block text-xs font-medium text-gray-600">
                    To
                    <input
                      value={draft.to}
                      onChange={(event) =>
                        setDraft({ ...draft, to: event.target.value })
                      }
                      placeholder="name@example.com, another@example.com"
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal focus:border-sky-500 focus:outline-none"
                    />
                  </label>
                  <label className="mt-2 block text-xs font-medium text-gray-600">
                    Cc
                    <input
                      value={draft.cc}
                      onChange={(event) =>
                        setDraft({ ...draft, cc: event.target.value })
                      }
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal focus:border-sky-500 focus:outline-none"
                    />
                  </label>
                </>
              )}

              {draft.mode === "compose" && (
                <label className="mt-2 block text-xs font-medium text-gray-600">
                  Subject
                  <input
                    value={draft.subject}
                    onChange={(event) =>
                      setDraft({ ...draft, subject: event.target.value })
                    }
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal focus:border-sky-500 focus:outline-none"
                  />
                </label>
              )}

              <label className="mt-2 block text-xs font-medium text-gray-600">
                Message
                <textarea
                  value={draft.body}
                  onChange={(event) =>
                    setDraft({ ...draft, body: event.target.value })
                  }
                  rows={8}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal focus:border-sky-500 focus:outline-none"
                />
              </label>

              {draft.mode === "forward" && (
                <p className="mt-1 text-xs text-gray-500">
                  The original message is quoted below whatever you write here,
                  with its sender and date.
                </p>
              )}

              {refusal && (
                <p
                  role="alert"
                  className="mt-3 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                >
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>{refusal} It was not sent.</span>
                </p>
              )}

              <div className="mt-3 flex items-center gap-2">
                <button
                  type="submit"
                  disabled={send.isPending}
                  className="flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:bg-gray-300"
                >
                  {send.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" /> Send
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDraft(null);
                    setRefusal(null);
                  }}
                  className="rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
                >
                  Discard
                </button>
              </div>
            </form>
          )}
        </article>
      </div>
    </div>
  );
}
